import addDays from 'date-fns/addDays';
import parse from 'date-fns/parse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  DROPOFF_NOTE_TEXT,
  formatPickupAddress,
  getDropoffRestrictedDates,
  getLeadTimeDays,
  NOVUS_PICKUP_ADDRESS,
  textFieldSx,
  toDateOnly,
} from '@components/Checkout/steps/InstallationStep/utils/installationStepUtils';
import type { SelfInstallSectionProps } from '@components/Checkout/steps/InstallationStep/InstallationStep.types';
import DatePickerField from '@shared/components/inputs/DatePickerField';
import InfoBannerBlock from '@shared/components/feedback/InfoBannerBlock/InfoBannerBlock';
import TextFieldLabel from '@shared/components/inputs/TextFieldLabel/TextFieldLabel';
import type { HardwareDelivery } from '@t/checkout.types';

export const SelfInstallSection = ({
  formik,
  technicianOptions,
  pickupAddress,
  restrictionDescription,
}: SelfInstallSectionProps) => {
  const { values, errors, touched, setValues, setFieldValue, setFieldTouched } = formik;
  const displayPickupAddress = pickupAddress ? formatPickupAddress(pickupAddress) : '';
  const dateError = touched.selectedDate && errors.selectedDate ? String(errors.selectedDate) : '';
  const leadTimeDays = getLeadTimeDays(technicianOptions, values.installationMethod, values.hardwareDelivery);
  const disabledDates =
    values.installationMethod === 'SELF' && values.hardwareDelivery === 'DROPOFF'
      ? getDropoffRestrictedDates(technicianOptions)
      : [];
  const minDate = addDays(new Date(), leadTimeDays);

  const handleDateChange = (dateStr: string) => {
    setFieldTouched('selectedDate', true, false);

    if (!dateStr) {
      setValues({ ...values, selectedDate: null, selectedTimeSlot: null, selectedSlotId: null });
      return;
    }

    const localDate = parse(dateStr, 'yyyy-MM-dd', new Date());
    const iso = localDate.toISOString();
    setValues({
      ...values,
      selectedDate: localDate,
      selectedTimeSlot: { startTime: iso, endTime: iso },
      selectedSlotId: iso,
    });
  };

  const handleHardwareDeliveryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('hardwareDelivery', e.target.value as HardwareDelivery);
  };

  const handleDeliveryInstructionsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFieldValue('deliveryInstructions', e.target.value);
  };

  return (
    <>
      <Stack gap="10px">
        <TextFieldLabel label="How Would You Like to Receive Your Equipment?" />

        <RadioGroup value={values.hardwareDelivery} onChange={handleHardwareDeliveryChange} sx={{ gap: '10px' }}>
          <FormControlLabel
            value="PICKUP"
            label="Pick up in person at a Novus location"
            control={<Radio disableFocusRipple disableRipple />}
          />

          {values.hardwareDelivery === 'PICKUP' && (
            <InfoBannerBlock title="Pickup Location" description={displayPickupAddress || NOVUS_PICKUP_ADDRESS} />
          )}

          <FormControlLabel
            value="DROPOFF"
            label="Have it delivered to your service address"
            control={<Radio disableFocusRipple disableRipple />}
          />

          {values.hardwareDelivery === 'DROPOFF' && <InfoBannerBlock description={DROPOFF_NOTE_TEXT} />}
        </RadioGroup>
      </Stack>

      <Stack gap="6px">
        <TextFieldLabel label="Schedule Your Service Activation" />
        <Typography variant="body.sm" sx={{ lineHeight: '150%' }}>
          When do you want your service to start?
        </Typography>
        <DatePickerField
          ariaLabel="Schedule Your Service Activation"
          value={values.selectedDate ? toDateOnly(values.selectedDate) : ''}
          onChange={handleDateChange}
          onBlur={() => setFieldTouched('selectedDate', true)}
          minDate={minDate}
          disabledDates={disabledDates}
          placeholder="Select Date"
          error={!!dateError}
          helperText={dateError}
          sx={textFieldSx}
        />
      </Stack>

      {values.hardwareDelivery === 'DROPOFF' && (
        <>
          {restrictionDescription && (
            <InfoBannerBlock title="Important Instructions" description={restrictionDescription} />
          )}

          <Stack gap="10px">
            <TextFieldLabel label="Delivery Instructions" optional />
            <TextField
              sx={textFieldSx}
              multiline
              rows={3}
              placeholder="Let us know any details to help your delivery person, such as a buzzer number, gate code, or specific location to leave your equipment (e.g., 'leave by the side door'), etc..."
              value={values.deliveryInstructions}
              onChange={handleDeliveryInstructionsChange}
            />
          </Stack>
        </>
      )}
    </>
  );
};
